import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Validators } from '@angular/forms';
import { DEFAULT_LANGUAGE, IFormDelegate, IFormOptions, IProperty } from '@modeso/aumico-lib-core-fe';
import {
    FinancialLineItemCashFlowType,
    FinancialLineItemType,
    IFinancialLineItemDTO,
    IFinancialLineItemExtendedDTO,
} from '@modeso/types__aumico-lib-settings-be';
import { I18NextService } from 'angular-i18next';
import { Observable } from 'rxjs';
import { IFinancialLineItemFormData } from '../../interfaces/IFinancialLineItemFormData.interface';
import { SettingsProvider } from '../../providers/settings.provider';
import { getEntriesOfNumericEnum, getEntriesOfStringEnum } from '../../utils/EnumUtils';
import { getDebug, getTag } from '../../utils/debugUtil';
import { getLocalizationFromAsyncPipe } from '../../utils/localizationUtils';

const filename =
    'projects/aumico-lib-settings-fe/src/lib/components/financial-line-item-form/financial-line-item-form.component.ts';
const debug = getDebug(filename);
const TAG = getTag(filename);

type SmallFinancialLineItemType = IFinancialLineItemDTO;
@Component({
    // tslint:disable-next-line: component-selector
    selector: 'settings-financial-line-item-form-component',
    templateUrl: './financial-line-item-form.component.html',
})
export class FinancialLineItemFormComponent implements OnInit, IFormDelegate {
    protected subscriptions = [];
    public errorMessage: Observable<HttpErrorResponse>;

    constructor(private provider: SettingsProvider, private i18nextService: I18NextService) {}

    @Input()
    languages: string[] = ['de-ch', 'en-us'];

    @Input()
    set data(data: IFinancialLineItemExtendedDTO) {
        this.handleItemUpdated(data);
    }

    @Output()
    public savePressed = new EventEmitter<IFinancialLineItemDTO>();

    DATA: IFinancialLineItemFormData;
    options: IFormOptions;

    // This adapts the form according to the type of action
    private handleItemUpdated(inputItem: IFinancialLineItemExtendedDTO) {
        const item = { ...inputItem };

        item.description = item.description || {};
        for (const lang of this.languages) {
            item.description[lang] = item.description[lang] || '';
        }

        debug('ngOnInit getAll result');
        debug(item);
        this.prepareFields(item._id == null ? 'create' : 'edit');

        this.DATA = {
            ...item,
            cashflowOption: getEntriesOfNumericEnum(FinancialLineItemCashFlowType)
                .map(([key, value]) => ({ id: value.toString(), name: key }))
                .find((i) => i.id === item.cashflow?.toString()),
            typeOption: getEntriesOfStringEnum(FinancialLineItemType)
                .map(([key, value]) => ({ id: value, name: key }))
                .find((i) => i.id === item.type),
            get item() {
                return item;
            },
        };
    }

    private mapItem(item: IFinancialLineItemFormData): IFinancialLineItemDTO {
        // map IFinancialLineItemExtendedDTO to IFinancialLineItemDTO
        const output: IFinancialLineItemDTO = {
            _id: item['_id'],
            name: item['name'],
            description: item['description'],
            type: item.typeOption.id as FinancialLineItemType,
            position: typeof item['position'] === 'string' ? parseInt(item['position'], 10) : item['position'],
            cashflow: +item.cashflowOption.id,
        };
        return output;
    }

    public onSubmit(result: IFinancialLineItemFormData): void {
        const item = this.mapItem(result);
        this.savePressed.emit(item);
    }

    async prepareFields(action: 'create' | 'edit' = 'create') {
        this.options = {
            properties: [
                {
                    name: await getLocalizationFromAsyncPipe(
                        this.i18nextService,
                        'aumico_settings_financialLineItem_id',
                    ),
                    type: 'label',
                    property: '_id',
                    required: false,
                },
                {
                    name: await getLocalizationFromAsyncPipe(
                        this.i18nextService,
                        'aumico_settings_financialLineItem_version',
                    ),
                    type: 'label',
                    property: 'version',
                    required: false,
                },
                {
                    name: await getLocalizationFromAsyncPipe(
                        this.i18nextService,
                        'aumico_settings_financialLineItem_owner',
                    ),
                    type: 'label',
                    property: 'owner',
                    required: false,
                },
                {
                    name: await getLocalizationFromAsyncPipe(
                        this.i18nextService,
                        'aumico_settings_financialLineItem_name',
                    ),
                    type: action === 'create' ? 'text' : 'label',
                    property: 'name',
                    required: true,
                },
                {
                    name: await getLocalizationFromAsyncPipe(
                        this.i18nextService,
                        'aumico_settings_financialLineItem_description',
                    ),
                    type: 'translatable-text',
                    property: 'description',
                    required: true,
                    languages: [DEFAULT_LANGUAGE],
                },
                {
                    name: await getLocalizationFromAsyncPipe(
                        this.i18nextService,
                        'aumico_settings_financialLineItem_cashflow',
                    ),
                    type: 'select',
                    options: getEntriesOfNumericEnum(FinancialLineItemCashFlowType).map(([key, value]) => ({
                        id: value.toString(),
                        name: key,
                    })),
                    property: 'cashflowOption',
                    required: true,
                    multiple: false,
                },
                {
                    name: await getLocalizationFromAsyncPipe(
                        this.i18nextService,
                        'aumico_settings_financialLineItem_type',
                    ),
                    type: 'select',
                    options: getEntriesOfStringEnum(FinancialLineItemType).map(([key, value]) => ({
                        id: value,
                        name: key,
                    })),
                    property: 'typeOption',
                    required: true,
                    multiple: false,
                },
                {
                    name: await getLocalizationFromAsyncPipe(
                        this.i18nextService,
                        'aumico_settings_financialLineItem_position',
                    ),
                    type: 'text',
                    property: 'position',
                    required: true,
                    extraValidators: [
                        Validators.min(1000),
                        Validators.minLength(4),
                        Validators.pattern(/^-?(0|[1-9]\d*)?$/),
                    ],
                } as IProperty,
            ],
            submitText: await getLocalizationFromAsyncPipe(this.i18nextService, 'aumico_button_save'),
            invalidText: await getLocalizationFromAsyncPipe(
                this.i18nextService,
                'aumico_settings_financialLineItem_invalidText',
            ),
        };
    }

    public ngOnInit(): void {
        this.prepareFields();
        this.subscriptions.push(
            this.provider
                .financialLineItemError$()
                .subscribe((error) => (this.errorMessage = new Observable((observer) => observer.next(error)))),
        );
    }
}
